import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import apiService from '../../service/ApiService';
import type { EnvironmentRequest } from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

type SiteType = 'CUSTOMER_FACING' | 'ISS_INTERNAL';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  facilitationSupport?: boolean;
  successCriteria?: {
    criterion: string;
    measurement: string;
  }[];
  environment?: {
    siteType?: SiteType;
    testSiteAvailable?: boolean | 'NOT_NEEDED';
  };
};

const SITE_OPTIONS: { id: SiteType; label: string }[] = [
  { id: 'CUSTOMER_FACING', label: 'Customer-facing' },
  { id: 'ISS_INTERNAL', label: 'ISS-Internal' },
];
function toTestSiteApi(
  value: boolean | 'NOT_NEEDED'
): EnvironmentRequest['testSite'] {
  if (value === true) return 'REQUIRED_AND_AVAILABLE';
  if (value === false) return 'REQUIRED_NOT_IDENTIFIED';
  return 'NOT_REQUIRED';
}

const PocEnvironmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [siteType, setSiteType] = useState<SiteType | null>(
    state.environment?.siteType ?? null
  );
  const [testSiteAvailable, setTestSiteAvailable] = useState<
    boolean | 'NOT_NEEDED' | null
  >(
    state.environment?.testSiteAvailable === undefined
      ? null
      : state.environment.testSiteAvailable === 'NOT_NEEDED'
        ? 'NOT_NEEDED'
        : state.environment.testSiteAvailable
  );
  const [attempted, setAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!siteType) next.siteType = 'Select Customer-facing or ISS-Internal.';
    if (testSiteAvailable === null) {
      next.testSite = 'Select Yes or No.';
    }
    return next;
  }, [siteType, testSiteAvailable]);

  const isValid = Object.keys(errors).length === 0;

  const environment = {
    siteType: siteType ?? undefined,
    testSiteAvailable:
      testSiteAvailable === null ? undefined : testSiteAvailable,
  };

  const handleNext = async () => {
    setAttempted(true);
    setSubmitError(null);
    if (!isValid) return;
  
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
    if (!siteType || testSiteAvailable === null) return;
  
    setIsSubmitting(true);
    try {
      await apiService.saveEnvironment(pocId, {
        pocLocation: siteType,
        testSite: toTestSiteApi(testSiteAvailable),
      });
  
      navigate('/pocs/new/preparation/nda', {
        state: {
          ...state,
          environment,
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save environment. Check Network tab (PoC must be in Preparation).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step initiation__step--active">
            Step 2
          </span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            PoC site
          </Headline>
          <Lead>Step 2 — Tell us where this Proof of Concept will be run.</Lead>

          <form
            className="bj-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleNext();
            }}
          >
            <fieldset className="bj-field">
              <legend className="bj-field__label">
                Will this PoC be conducted at a customer location
                (customer-facing) or within one of our internal sites
                (internal)?
              </legend>
              <p className="initiation__note">
                Note: It will determine further flow of the PoC process.
              </p>
              <div className="bj-choice-list">
                {SITE_OPTIONS.map((option) => (
                  <label key={option.id} className="bj-choice">
                    <input
                      type="radio"
                      name="poc-site-type"
                      checked={siteType === option.id}
                      onChange={() => setSiteType(option.id)}
                    />
                    <span>
                      <strong>{option.label}</strong>
                    </span>
                  </label>
                ))}
              </div>
              {attempted && errors.siteType ? (
                <span className="bj-field__error">{errors.siteType}</span>
              ) : null}
            </fieldset>

            {siteType ? (
              <fieldset className="bj-field">
                <legend className="bj-field__label">
                  Do you already have a test site available where you can run
                  the PoC?
                </legend>
                <div className="bj-choice-list">
                  <label className="bj-choice">
                    <input
                      type="radio"
                      name="poc-test-site"
                      checked={testSiteAvailable === true}
                      onChange={() => setTestSiteAvailable(true)}
                    />
                    <span>
                      <strong>Yes</strong>
                    </span>
                  </label>
                  <label className="bj-choice">
                    <input
                      type="radio"
                      name="poc-test-site"
                      checked={testSiteAvailable === false}
                      onChange={() => setTestSiteAvailable(false)}
                    />
                    <span>
                      <strong>No</strong>
                    </span>
                  </label>
                  <label className="bj-choice">
                    <input
                      type="radio"
                      name="poc-test-site"
                      checked={testSiteAvailable === 'NOT_NEEDED'}
                      onChange={() => setTestSiteAvailable('NOT_NEEDED')}
                    />
                    <span>
                      <strong>No need for a test site</strong>
                    </span>
                  </label>
                </div>
                {attempted && errors.testSite ? (
                  <span className="bj-field__error">{errors.testSite}</span>
                ) : null}
              </fieldset>
            ) : null}

            {testSiteAvailable === false ? (
              <div className="phase-gate" role="status">
                <p className="phase-gate__title">No test site yet</p>
                <p>Please contact the Digital Innovations department.</p>
                <p>Align on a lighthouse site, then continue to Assessment.</p>
              </div>
            ) : null}

            {testSiteAvailable === true ||
            testSiteAvailable === 'NOT_NEEDED' ? (
              <p className="scorecard__notice" role="status">
                You can continue to Assessment (NDA and related checks).
              </p>
            ) : null}

            {submitError ? (
              <p className="bj-field__error">{submitError}</p>
            ) : null}

            <div className="initiation__actions">
              <button
                type="button"
                className="initiation__link-back"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() =>
                  navigate('/pocs/new/preparation/success-criteria', {
                    state: {
                      ...state,
                      environment,
                    },
                  })
                }
              >
                ← Back to success criteria
              </button>

              <button
                type="submit"
                className="initiation__next"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : 'Next →'}
              </button>
            </div>
          </form>
        </Section>
      </div>
    </div>
  );
};

export default PocEnvironmentPage;
